/**
 * <collection-category-cards>
 * Drives a growing scroll-progress bar for the Popular / Related Categories card row,
 * matching the shared scroll-progress-bar (Popular Brands): the fill starts at the
 * viewport's share of the content and grows to 100% at the end of scroll.
 * Progressive enhancement: without JS the row still scrolls, the bar just stays empty.
 */
if (!customElements.get('collection-category-cards')) {
  customElements.define(
    'collection-category-cards',
    class CollectionCategoryCards extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.cat-cards__track');
        this.scrollbar = this.querySelector('.scroll-progress-bar');
        this.fill = this.querySelector('.progress-bar');
        this.controls = this.querySelector('.cat-cards__controls');
        this.prevBtn = this.querySelector('.cat-cards__nav--prev');
        this.nextBtn = this.querySelector('.cat-cards__nav--next');

        if (!this.track || !this.scrollbar || !this.fill) return;

        this.update = this.update.bind(this);
        this.track.addEventListener('scroll', () => requestAnimationFrame(this.update), { passive: true });
        window.addEventListener('resize', this.update);
        if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.page(-1));
        if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.page(1));
        this.initDragToScroll();
        this.initProgressDrag();
        this.update();
      }

      // Advance roughly one viewport per arrow click.
      page(direction) {
        this.track.scrollBy({ left: direction * this.track.clientWidth * 0.9, behavior: 'smooth' });
      }

      // Click-and-drag to scroll the row (like grabbing it). Suppresses the card
      // link click when the pointer actually dragged.
      initDragToScroll() {
        let down = false;
        let dragged = false;
        let startX = 0;
        let startScroll = 0;

        this.track.addEventListener('pointerdown', (e) => {
          if (e.pointerType === 'touch' || e.button !== 0) return; // native touch already scrolls
          down = true;
          dragged = false;
          startX = e.clientX;
          startScroll = this.track.scrollLeft;
          this.track.classList.add('is-dragging');
        });

        this.track.addEventListener('pointermove', (e) => {
          if (!down) return;
          const dx = e.clientX - startX;
          if (Math.abs(dx) > 3) dragged = true;
          this.track.scrollLeft = startScroll - dx;
        });

        const end = () => {
          if (!down) return;
          down = false;
          this.track.classList.remove('is-dragging');
        };
        this.track.addEventListener('pointerup', end);
        this.track.addEventListener('pointerleave', end);
        this.track.addEventListener('pointercancel', end);

        // Block the click that fires after a drag so it doesn't follow the card link.
        this.track.addEventListener(
          'click',
          (e) => {
            if (dragged) {
              e.preventDefault();
              e.stopPropagation();
              dragged = false;
            }
          },
          true
        );
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.update);
      }

      // Drag the progress bar itself to scrub the row (like a native scrollbar thumb).
      initProgressDrag() {
        let down = false;
        const scrub = (clientX) => {
          const rect = this.scrollbar.getBoundingClientRect();
          if (rect.width <= 0) return;
          let ratio = (clientX - rect.left) / rect.width;
          ratio = Math.min(1, Math.max(0, ratio));
          this.track.scrollLeft = ratio * (this.track.scrollWidth - this.track.clientWidth);
        };

        this.scrollbar.addEventListener('pointerdown', (e) => {
          if (e.button !== 0) return;
          down = true;
          this.scrollbar.classList.add('is-dragging');
          try { this.scrollbar.setPointerCapture(e.pointerId); } catch (err) {}
          scrub(e.clientX);
        });
        this.scrollbar.addEventListener('pointermove', (e) => {
          if (down) scrub(e.clientX);
        });
        const end = (e) => {
          if (!down) return;
          down = false;
          this.scrollbar.classList.remove('is-dragging');
          try { this.scrollbar.releasePointerCapture(e.pointerId); } catch (err) {}
        };
        this.scrollbar.addEventListener('pointerup', end);
        this.scrollbar.addEventListener('pointercancel', end);
      }

      update() {
        const overflow = this.track.scrollWidth - this.track.clientWidth;

        // Nothing to scroll — hide the whole control (bar + arrows).
        if (overflow <= 1) {
          if (this.controls) this.controls.style.display = 'none';
          return;
        }
        if (this.controls) this.controls.style.display = '';

        const total = this.track.scrollWidth;
        const viewport = this.track.clientWidth;
        const initial = (viewport / total) * 100; // fill width at scroll start
        const scrollProgress = (this.track.scrollLeft / overflow) * (100 - initial);
        const progress = Math.min(100, Math.max(initial, initial + scrollProgress));

        this.fill.style.width = `${progress}%`;

        // Disable the arrows at the start/end.
        const atStart = this.track.scrollLeft <= 1;
        const atEnd = Math.ceil(this.track.scrollLeft + viewport) >= total;
        if (this.prevBtn) this.prevBtn.classList.toggle('swiper-button-disabled', atStart);
        if (this.nextBtn) this.nextBtn.classList.toggle('swiper-button-disabled', atEnd);
      }
    }
  );
}
